import React from 'react';
import type { Area, Branch } from '../data/financialData';
import { useData } from '../DataContext';

export type DatePreset = string; // 'all' | 'custom' | any key of FY_PRESETS

interface FiltersBarProps {
  area: Area;
  onAreaChange: (a: Area) => void;
  branch: string;
  onBranchChange: (b: string) => void;
  datePreset: DatePreset;
  onDatePresetChange: (preset: DatePreset) => void;
  startMonth: string;
  endMonth: string;
  onStartMonthChange: (month: string) => void;
  onEndMonthChange: (month: string) => void;
  onExportPDF: () => void;
  onExportExcel: () => void;
  onExportPPT?: () => void;
}

const AREA_ORDER: Area[] = ['Depok', 'Bogor', 'Bekasi'];

const FiltersBar: React.FC<FiltersBarProps> = ({
  area, onAreaChange,
  branch, onBranchChange,
  datePreset, onDatePresetChange,
  startMonth, endMonth, onStartMonthChange, onEndMonthChange,
  onExportPDF, onExportExcel, onExportPPT,
}) => {
  const { MONTHS, AREAS, FY_PRESETS, branches } = useData();
  const presets: { id: DatePreset; label: string }[] = [
    { id: 'all', label: 'Semua' },
    ...Object.entries(FY_PRESETS).map(([id, p]) => ({ id, label: p.label })),
    { id: 'custom', label: 'Kustom' },
  ];

  const branchesInArea: Branch[] = area === 'ALL'
    ? branches
    : branches.filter(b => b.area === area);

  const branchLabel = (b: Branch) =>
    `${b.code} — ${b.name}${b.status === 'Closed' ? ' (Tutup)' : ''}`;

  return (
    <div className="filters-bar">
      <div className="filter-group">
        <span className="filter-label">📍 Area:</span>
        <select
          className="filter-select"
          value={area}
          onChange={e => onAreaChange(e.target.value as Area)}
        >
          {AREAS.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
      </div>

      <div className="filter-group">
        <span className="filter-label">🏢 Cabang:</span>
        <select
          className="filter-select"
          value={branch}
          onChange={e => onBranchChange(e.target.value)}
        >
          <option value="ALL">Semua Cabang</option>
          {area === 'ALL'
            ? AREA_ORDER.map(a => {
                const group = branches.filter(b => b.area === a);
                return (
                  <optgroup key={a} label={a}>
                    {group.map(b => (
                      <option key={b.code} value={b.code}>{branchLabel(b)}</option>
                    ))}
                  </optgroup>
                );
              })
            : branchesInArea.map(b => (
                <option key={b.code} value={b.code}>{branchLabel(b)}</option>
              ))
          }
        </select>
      </div>

      <div className="filter-group">
        <span className="filter-label">📅 Periode:</span>
        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
          {presets.map(p => (
            <button
              key={p.id}
              className={`filter-preset-btn ${datePreset === p.id ? 'active' : ''}`}
              onClick={() => onDatePresetChange(p.id)}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {datePreset === 'custom' && (
        <div className="filter-group">
          <span className="filter-label">Dari:</span>
          <select
            className="filter-select"
            value={startMonth}
            onChange={e => onStartMonthChange(e.target.value)}
          >
            {MONTHS.map(m => (
              <option key={m.month} value={m.month}>{m.label}</option>
            ))}
          </select>
          <span className="filter-label">Sampai:</span>
          <select
            className="filter-select"
            value={endMonth}
            onChange={e => onEndMonthChange(e.target.value)}
          >
            {MONTHS.map(m => (
              <option key={m.month} value={m.month}>{m.label}</option>
            ))}
          </select>
        </div>
      )}

      <div style={{ flex: 1 }} />

      <div className="quick-actions">
        <button className="btn btn-secondary" onClick={onExportPDF} id="export-pdf-btn">
          📄 Export PDF
        </button>
        <button className="btn btn-secondary" onClick={onExportExcel} id="export-excel-btn">
          📊 Export Excel
        </button>
        {onExportPPT && (
          <button className="btn btn-secondary" onClick={onExportPPT} id="export-ppt-btn">
            🎯 Export PPT
          </button>
        )}
      </div>
    </div>
  );
};

export default FiltersBar;
